import React from 'react';
import Image from 'next/image';
import mean from 'lodash/mean';
import round from 'lodash/round';
import moment from 'moment/moment';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ReactCardFlip from 'react-card-flip';
import CityPicker from './CityPicker';
import CompareTable from './CompareTable';
import { LightBulbIcon } from '@/components/Icon';
import {
  TAB_CITY_COMPARISON,
  WORK_EXPERIENCE_ARRAY,
  WORK_EXPERIENCE_MAP,
} from '@/constants/job-title';
import {
  JobOpenDetailsType,
  JobSalaryDetailsType,
  LocationDataValue,
} from '@/context/job-title';
import { JobTitleContext } from '@/context/create-context';
import { getCityComparison } from '@/api/tanzhi';
import { numberRound } from '@/utils/numbers';
import GeneralChart from '@/components/job-title/GeneralChart';
import CityComparisonImg from '@/assets/job-title/city-comparison.svg';
import { trackClickEvent } from '@/utils/site-track';

interface SingleCityResultRaw {
  name: string;
  location: LocationDataValue;
  job_open: JobOpenDetailsType;
  job_salary: JobSalaryDetailsType;
}
type CityComparisonResultRaw = SingleCityResultRaw[];

const CompareButton = (props: {
  id: string;
  setComparisonResult: React.Dispatch<
    React.SetStateAction<CityComparisonResultRaw>
  >;
  setFlipped: React.Dispatch<React.SetStateAction<boolean>>;
  city: string;
  comparedCity: string;
}) => {
  const { id, city, comparedCity, setComparisonResult, setFlipped } = props;

  const handleCompareCity = async (e: React.MouseEvent<HTMLButtonElement>) => {
    if (city && comparedCity && city === comparedCity) return;
    const { data } = await getCityComparison(id, city, comparedCity);
    trackClickEvent(e);
    setComparisonResult(data);
    setFlipped(true);
  };

  return (
    <Button
      color="primary"
      variant="contained"
      disabled={!city || !comparedCity || city === comparedCity}
      className="!mt-6 !w-full !h-10 !font-semibold !whitespace-nowrap disabled:!bg-web-blue-500 disabled:!text-white disabled:!opacity-80 tablet:!float-right tablet:!mt-4 tablet:!w-auto tablet:!h-auto tablet:!font-normal"
      onClick={(e) => handleCompareCity(e)}
      data-ele-id="button"
      data-ele-group={`${TAB_CITY_COMPARISON}-comparison_button`}
    >
      <span className="mx-2">对比</span>
    </Button>
  );
};

const QueryBox = (props: {
  city: string;
  comparedCity: string;
  setCity: React.Dispatch<React.SetStateAction<string>>;
  setComparedCity: React.Dispatch<React.SetStateAction<string>>;
  children: React.ReactNode;
}) => {
  const { city, comparedCity, setCity, setComparedCity, children } = props;
  const [errorText, setErrorText] = React.useState('');

  const handleErrorCheck = () => {
    const sameCity = !!city && !!comparedCity && city === comparedCity;
    setErrorText(sameCity ? '请选择不同的城市进行对比' : '');
  };

  React.useEffect(() => {
    handleErrorCheck();
  }, [city, comparedCity]);

  return (
    <div className="grid grid-cols-[1fr_auto] shadow-[0px_7px_18px_rgba(0,0,0,0.05)]">
      <div className="flex flex-col justify-center my-6 ml-4 tablet:my-12 tablet:ml-12">
        <div className="mb-6 font-semibold whitespace-nowrap tablet:font-normal">
          选择你感兴趣的城市进行对比
        </div>
        <>
          <div className="flex flex-col items-start max-w-[208px] grid-cols-[1fr_auto_1fr] tablet:max-w-[initial] tablet:grid">
            <CityPicker value={city} onChange={setCity} />
            <span className="px-2 py-3 mx-auto text-xl font-semibold tablet:text-base tablet:font-normal tablet:py-4">
              vs
            </span>
            <CityPicker
              value={comparedCity}
              onChange={setComparedCity}
              helperText={errorText}
            />
          </div>
          <div className="hidden tablet:block">{children}</div>
        </>
      </div>
      <div className="my-auto mx-8 pt-7 pb-2 tablet:m-12 tablet:py-0 tablet:mx-12">
        <Image src={CityComparisonImg} alt="city comparison image" />
        <div className="tablet:hidden">{children}</div>
      </div>
    </div>
  );
};

const Result = (props: {
  comparisonResult: CityComparisonResultRaw;
  setComparisonResult: React.Dispatch<
    React.SetStateAction<CityComparisonResultRaw>
  >;
  setFlipped: React.Dispatch<React.SetStateAction<boolean>>;
  city: string;
  comparedCity: string;
}) => {
  const {
    comparisonResult,
    city,
    setComparisonResult,
    setFlipped,
    comparedCity,
  } = props;

  const decorate = (value: number) =>
    (value > 0
      ? '高' + Math.floor(value * 100)
      : '低' + -Math.floor(value * 100)) + '%';

  const [
    {
      job_open: { current_count: currentCount1 },
      job_salary: { avg: avg1 },
      location: { house_price: housePrice1 },
    },
    {
      job_open: { current_count: currentCount2 },
      job_salary: { avg: avg2 },
      location: { house_price: housePrice2 },
    },
  ] = comparisonResult;

  const comparedData = {
    salary: avg1 && avg2 ? (avg1 - avg2) / avg2 : NaN,
    newJob:
      currentCount1 && currentCount2
        ? (currentCount1 - currentCount2) / currentCount1
        : NaN,
    housePrice:
      housePrice1 && housePrice2
        ? (housePrice1 - housePrice2) / housePrice2
        : NaN,
  };

  const renderJobIncreaseCompareBody = () =>
    WORK_EXPERIENCE_ARRAY.map((experience) => {
      const avgCalculator = (data: any) => {
        const countTmp: Record<string, [number, number]> = {};
        if (!data) return countTmp;
        for (const experienceItem of data) {
          if (!countTmp[experienceItem.name]) {
            countTmp[experienceItem.name] = [0, 0];
          }
          countTmp[experienceItem.name][0] += experienceItem.value;
          countTmp[experienceItem.name][1] += 1;
        }
        return countTmp;
      };

      const cityCount = avgCalculator(
        comparisonResult[0].job_open.avg_new_job_details?.monthly
          ?.by_experience,
      );
      const comparedCityCount = avgCalculator(
        comparisonResult[1].job_open.avg_new_job_details?.monthly
          ?.by_experience,
      );
      return {
        title: WORK_EXPERIENCE_MAP[experience],
        city:
          (cityCount[experience] &&
            Math.floor(
              cityCount[experience][0] / cityCount[experience][1] || 0,
            )) ||
          '暂无',
        comparedCity:
          (comparedCityCount[experience] &&
            Math.floor(
              comparedCityCount[experience][0] /
                comparedCityCount[experience][1] || 0,
            )) ||
          '暂无',
      };
    });

  const renderSalaryCompareBody = () =>
    WORK_EXPERIENCE_ARRAY.map((experience) => {
      const calculateExperienceSalaryDetail = (index: number) => {
        const salary = round(
          mean(
            Object.values(
              comparisonResult[index].job_salary
                .avg_value_by_experience_details?.[experience] ?? NaN,
            ),
          ),
          -2,
        );
        return salary ? '￥' + salary : '暂无';
      };
      return {
        title: WORK_EXPERIENCE_MAP[experience],
        city: calculateExperienceSalaryDetail(0),
        comparedCity: calculateExperienceSalaryDetail(1),
      };
    });

  return (
    <div className="my-6 text-sm tablet:mb-10">
      <div className="flex tablet:mb-6">
        <div className="mt-0.5 tablet:mt-3">
          <IconButton
            className="p-0 mr-1 tablet:mr-2"
            onClick={() => {
              setFlipped(false);
              setTimeout(() => {
                setComparisonResult([]);
              }, 500);
            }}
          >
            <ChevronLeft />
          </IconButton>
        </div>
        <div className="tablet:mt-3">
          <p className="text-lg font-semibold tablet:text-base tablet:font-normal">
            选择你感兴趣的城市进行对比
          </p>
          {comparedData.salary ||
          comparedData.newJob ||
          comparedData.housePrice ? (
            <p className="text-base align-middle mt-3 tablet:text-xs">
              <LightBulbIcon className="inline mt-2" />
              {city}的
              {comparedData.salary
                ? `的平均月薪比${comparedCity}${decorate(comparedData.salary)}`
                : ''}
              {comparedData.salary && comparedData.newJob ? '，' : ''}
              {comparedData.newJob
                ? `平均月新增职位比${comparedCity}${decorate(
                    comparedData.newJob,
                  )}`
                : ''}
              {(comparedData.salary || comparedData.newJob) &&
              comparedData.housePrice
                ? '，'
                : ''}
              {comparedData.housePrice
                ? `平均房价比${comparedCity}${decorate(
                    comparedData.housePrice,
                  )}`
                : ''}
            </p>
          ) : null}
        </div>
      </div>
      <CompareTable
        tableName=""
        header={[city, comparedCity].map((cityName) => (
          <span
            key={cityName}
            className="font-medium text-center text-base tablet:text-lg"
          >
            {cityName}
          </span>
        ))}
        body={[]}
        hideExpand={true}
      />
      <CompareTable
        tableName="平均月薪"
        header={comparisonResult.map((item, index) => {
          const jobSalaryAvg = round(item.job_salary.avg, -2);
          const avgSalaryByMonth =
            item.job_salary?.avg_salary_details?.by_month;
          const lastSalaryByMonth =
            avgSalaryByMonth && avgSalaryByMonth.length > 0
              ? avgSalaryByMonth[avgSalaryByMonth.length - 1].name
              : '';
          return (
            <p
              key={index}
              className="font-medium text-center grid justify-items-center"
            >
              <span className="pb-1">
                {jobSalaryAvg ? `￥${jobSalaryAvg}` : '暂无'}
              </span>
              {lastSalaryByMonth && (
                <span className="text-xs text-black/65 font-normal max-w-40">
                  {`数据截止${moment(lastSalaryByMonth).format('yyyy年MM月')}，仅供参考`}
                </span>
              )}
            </p>
          );
        })}
        body={renderSalaryCompareBody()}
      />
      <CompareTable
        tableName="平均月新增职位"
        header={comparisonResult.map((item, index) => {
          const jobOpenByMonth = item.job_open?.new_job_details?.by_month;
          const lastOpenByMonth =
            jobOpenByMonth && jobOpenByMonth.length > 0
              ? jobOpenByMonth[jobOpenByMonth.length - 1].name
              : '';
          return (
            <p
              key={index}
              className="font-medium text-center grid justify-items-center"
            >
              <span className="pb-1">
                {item.job_open?.avg_new_job_details?.monthly?.count || '暂无'}
              </span>
              {lastOpenByMonth && (
                <span className="text-xs text-black/65 font-normal max-w-40">
                  {`数据截止${moment(lastOpenByMonth).format('yyyy年MM月')}，仅供参考`}
                </span>
              )}
            </p>
          );
        })}
        body={renderJobIncreaseCompareBody()}
      />
      <CompareTable
        tableName="平均房价"
        header={comparisonResult.map((item, index) => {
          const housePrice = numberRound(item.location.house_price);
          return (
            <span key={index} className="text-sm font-medium text-center">
              {housePrice ? `￥${housePrice}/平方` : '暂无'}
            </span>
          );
        })}
        body={[]}
        hideExpand={true}
      />
    </div>
  );
};

const ComparisonBox = () => {
  const {
    displayName,
    jobTitle: { id, city_name: cityName = '' },
  } = React.useContext(JobTitleContext);

  const [flipped, setFlipped] = React.useState(false);
  const [city, setCity] = React.useState<string>('');
  const [comparedCity, setComparedCity] = React.useState<string>('');
  const [comparisonResult, setComparisonResult] =
    React.useState<CityComparisonResultRaw>([]);

  React.useEffect(() => {
    if (cityName) {
      setCity(cityName);
    }
  }, [cityName]);

  return (
    <GeneralChart type="custom" title={`${displayName}去哪里工作更好？`}>
      <ReactCardFlip
        isFlipped={flipped}
        flipDirection="horizontal"
        cardStyles={{
          back: { overflow: 'hidden' },
          front: { overflow: 'hidden' },
        }}
      >
        <QueryBox
          city={city}
          comparedCity={comparedCity}
          setCity={setCity}
          setComparedCity={setComparedCity}
        >
          <CompareButton
            id={id}
            city={city}
            comparedCity={comparedCity}
            setFlipped={setFlipped}
            setComparisonResult={setComparisonResult}
          />
        </QueryBox>
        <>
          {comparisonResult.length && (
            <Result
              city={city}
              comparedCity={comparedCity}
              setFlipped={setFlipped}
              comparisonResult={comparisonResult}
              setComparisonResult={setComparisonResult}
            />
          )}
        </>
      </ReactCardFlip>
    </GeneralChart>
  );
};

export default ComparisonBox;
